import { Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { defaultTableSx } from "../tableFormatting";

dayjs.extend(utc);

interface SavingsBreakdownPoint {
  period_start: string;
  standard: string;
  heat_count: number;
  total_savings: number;
  specific_savings: number | null;
}

interface SavingsRow {
  id: number;
  standard: string;
  heat_count: number;
  total_savings: number;
  specific_savings: number | null;
}

interface SavingsTableProps {
  data: SavingsBreakdownPoint[];
  selectedDate: Date | null;
}

export const SavingsTable = ({ data, selectedDate }: SavingsTableProps) => {
  const { t } = useTenantTranslation();

  const rows = useMemo(
    () => buildRows(data, selectedDate),
    [data, selectedDate]
  );
  const totalsRow = useMemo(
    () => buildTotalsRow(data, selectedDate, t),
    [data, selectedDate, t]
  );
  const columns = useBuildColumns(t);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={totalsRow ? { bottom: [totalsRow] } : undefined}
        columns={columns}
      />
    </div>
  );
};

const buildRows = (
  data: SavingsBreakdownPoint[],
  selectedDate: Date | null
): SavingsRow[] => {
  if (!selectedDate) return [];
  const dateStr = dayjs.utc(selectedDate).format("YYYY-MM-DD");
  return data
    .filter(
      (d) =>
        d.period_start === dateStr && d.standard !== "all" && d.heat_count > 0
    )
    .map((d, i) => ({
      id: i,
      standard: d.standard,
      heat_count: d.heat_count,
      total_savings: d.total_savings,
      specific_savings: d.specific_savings,
    }));
};

const buildTotalsRow = (
  data: SavingsBreakdownPoint[],
  selectedDate: Date | null,
  t: TFunction
): SavingsRow | null => {
  if (!selectedDate) return null;
  const dateStr = dayjs.utc(selectedDate).format("YYYY-MM-DD");
  const allRow = data.find(
    (d) => d.period_start === dateStr && d.standard === "all"
  );
  if (!allRow) return null;
  return {
    id: -1,
    standard: t("Total"),
    heat_count: allRow.heat_count,
    total_savings: allRow.total_savings,
    specific_savings: allRow.specific_savings,
  };
};

const useBuildColumns = (t: TFunction): GridColDef[] => {
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();

  return [
    {
      field: "standard",
      headerName: t("standard"),
      flex: 1.5,
    },
    {
      field: "heat_count",
      type: "number",
      headerName: t("numberOfHeats"),
      flex: 1,
      align: "right",
      renderCell: (params: GridRenderCellParams<SavingsRow, number>) => (
        <Typography variant="body1" component="div">
          {params.value == null ? "" : formatNumber(0, true)(params.value)}
        </Typography>
      ),
    },
    {
      field: "total_savings",
      type: "number",
      headerName: `${t("savings")}${units("cost")}`,
      flex: 1.5,
      align: "right",
      renderCell: (params: GridRenderCellParams<SavingsRow, number>) => (
        <Typography variant="body1" component="div">
          {params.value == null ? "" : formatNumber(0, true)(params.value)}
        </Typography>
      ),
    },
    {
      field: "specific_savings",
      type: "number",
      headerName: `${t("savings")}${units("specific_cost")}`,
      flex: 1.5,
      align: "right",
      renderCell: (params: GridRenderCellParams<SavingsRow, number>) => (
        <Typography variant="body1" component="div">
          {params.value == null ? "" : formatNumber(2, true)(params.value)}
        </Typography>
      ),
    },
  ];
};
